import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SERVER_URL } from '../../constants/server-type';
import { Row, Col } from 'react-bootstrap';
import '../../styles/Buttons.css';
import '../../components/DataTable.css';
import '../../styles/template.css';

const ViewDataSet = () => {
  const { datasetId } = useParams();
  const navigate = useNavigate();

  // meta
  const [datasetName, setDatasetName] = useState('');
  const [template, setTemplate] = useState({ fields: [] });
  const [localVisibleColumns, setLocalVisibleColumns] = useState({});
  const [dropdownVisible, setDropdownVisible] = useState(false);

  // table state
  const [dataDocs, setDataDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [q, setQ] = useState('');

  // ---- helpers (no useMemo) ----
  const visibleFields = template.fields.filter(
    (f) => localVisibleColumns[f.label] && f.column === true
  );

  const getFldIndexByLabel = (label) => {
    const idx = template.fields.findIndex((f) => f.label === label);
    return idx >= 0 ? idx + 1 : null; // fld1..fldN
  };

  // 1) Load dataset + template (+ default column visibility)
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const dsRes = await fetch(`${SERVER_URL}/datasets/getDataSetById/${datasetId}`);
        const ds = await dsRes.json();
        setDatasetName(ds?.name || ds?.datasetName || '');

        const tplId = ds?.templateId?._id || ds?.templateId;
        if (tplId) {
          const tRes = await fetch(`${SERVER_URL}/templates/getTemplateById/${tplId}`);
          const t = await tRes.json();
          setTemplate(t || { fields: [] });

          const initialVis = (t?.fields || []).reduce((acc, f) => {
            acc[f.label] = !!f.column;
            return acc;
          }, {});
          setLocalVisibleColumns(initialVis);
        } else {
          setTemplate({ fields: [] });
        }
      } catch (err) {
        console.error('Meta load error:', err);
        setTemplate({ fields: [] });
      }
    };
    fetchMeta();
  }, [SERVER_URL, datasetId]);

  // 2) Load docs (server paging if available; else client fallback)
  useEffect(() => {
    const fetchDocs = async () => {
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(pageSize),
        });
        if (q) params.set('q', q);

        const res = await fetch(`${SERVER_URL}/datasets/viewdocs/${datasetId}?${params.toString()}`);
        const json = await res.json();

        if (Array.isArray(json)) {
          // Fallback: server returned a raw array; do client-side search + paging
          const fieldsCount = template.fields.length || 0;
          const needle = q.trim().toLowerCase();
          const filtered = needle
            ? json.filter((doc) => {
                for (let i = 1; i <= fieldsCount; i++) {
                  const val = String(doc[`fld${i}`] ?? '').toLowerCase();
                  if (val.includes(needle)) return true;
                }
                return false;
              })
            : json;

          const start = (page - 1) * pageSize;
          const end = start + pageSize;
          setDataDocs(filtered.slice(start, end));
          setTotal(filtered.length);
        } else {
          // Server-side paging shape: { items, total, ... }
          setDataDocs(Array.isArray(json?.items) ? json.items : []);
          setTotal(Number(json?.total || 0));
        }
      } catch (err) {
        console.error('Docs load error:', err);
        setDataDocs([]);
        setTotal(0);
      }
    };
    fetchDocs();
    // include template.fields so client-side search knows how many fldN to scan
  }, [SERVER_URL, datasetId, page, pageSize, q, template.fields]);

  return (
    <div className="datatable-container compact">
      {/* Header / controls (SmartDataTable-like) */}
      <div className="datatable-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h3 style={{ margin: 0 }}>Dataset: {datasetName || 'Loading...'}</h3>
          <div className="search-wrapper">
            <input
              className="datatable-search"
              placeholder="Search all visible fields…"
              value={q}
              onChange={(e) => { setPage(1); setQ(e.target.value); }}
            />
            {q && (
              <span className="clear-x" onClick={() => { setQ(''); setPage(1); }}>
                ×
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            className="btn-gray add-new-button"
            onClick={() => setDropdownVisible((v) => !v)}
          >
            Columns
          </button>
          <button
            className="btn-green add-new-button"
            onClick={() => navigate('/datasets/adddatadoc/' + datasetId)}
          >
            Add New Datadoc
          </button>
          <label htmlFor="pageSizeSelect" style={{ marginLeft: 10 }}>Rows:</label>
          <select
            id="pageSizeSelect"
            value={pageSize}
            onChange={(e) => { setPage(1); setPageSize(parseInt(e.target.value, 10)); }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Columns menu */}
      {dropdownVisible && (
        <div
          className="dropdown-menu"
          style={{
            maxHeight: 500,
            overflowY: 'auto',
            position: 'absolute',
            border: '1px solid #ccc',
            zIndex: 1000,
            right: '1rem',
            top: '110px',
            background: '#fff',
            padding: '6px 8px'
          }}
        >
          {template.fields
            .filter((f) => f.column)
            .map((field, i) => (
              <div key={i} className="dropdown-item" style={{ padding: '2px 6px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={!!localVisibleColumns[field.label]}
                    onChange={() =>
                      setLocalVisibleColumns((prev) => ({
                        ...prev,
                        [field.label]: !prev[field.label],
                      }))
                    }
                  />
                  {field.label}
                </label>
              </div>
            ))}
        </div>
      )}

      {/* Table */}
      <div className="datatable-scroll-wrapper">
        <table className="datatable">
          <thead className="sticky-header">
            <tr>
              {visibleFields.map((field, i) => (
                <th key={i}>{field.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataDocs.length > 0 ? (
              dataDocs.map((doc, ri) => (
                <tr key={doc._id || ri}>
                  {visibleFields.map((field, ci) => {
                    const fldIndex = getFldIndexByLabel(field.label);
                    const value = fldIndex ? doc[`fld${fldIndex}`] : null;
                    return <td key={ci}>{value ?? 'N/A'}</td>;
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={visibleFields.length}>No documents found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer / pagination */}
      <div className="datatable-footer">
        <div className="datatable-controls">
          <button onClick={() => setPage(1)} disabled={page <= 1}>First</button>
          <button onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page <= 1}>Previous</button>
          <span>Page {Math.min(page, Math.max(Math.ceil(total / pageSize), 1))} of {Math.max(Math.ceil(total / pageSize), 1)}</span>
          <button
            onClick={() => setPage((p) => {
              const max = Math.max(Math.ceil(total / pageSize), 1);
              return Math.min(p + 1, max);
            })}
            disabled={page >= Math.max(Math.ceil(total / pageSize), 1)}
          >
            Next
          </button>
          <button
            onClick={() => setPage(Math.max(Math.ceil(total / pageSize), 1))}
            disabled={page >= Math.max(Math.ceil(total / pageSize), 1)}
          >
            Last
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewDataSet;
